import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { CLASSES } from "../data/classes";
import {
  createCharacter,
  applyChestBonuses,
  applyPassivesAfterCombat,
  applyPassivesPerTile,
  applyPassivesAfterRoomDone,
  applyPassivesOnFloorAdvance,
  scaleEnemyStats,
  grantRunXp,
  addItemToInventory,
  removeItemFromInventory,
  applyItemEffectOutOfCombat,
} from "../utils/gameLogic";
import { ITEMS, rollItemFromChest } from "../data/items";
import { reportMissionEvent } from "../utils/missionsApi";
import { generateFloorMap } from "../utils/mapGenerator";
import { setRunThemeOffset, FLOOR_THEMES, themeForFloor } from "../utils/themes";
import { getBossForFloor, getRandomEnemy } from "../data/enemies";
import { generateShop } from "../data/shop";
import { generateChestContent, rollChestRarity } from "../data/chests";
import { generateSanctuaryPassive, rollSanctuaryRarity } from "../data/passives";
import { useAuth } from "./AuthContext";
import { useCoop } from "./CoopContext";
import { useProfile } from "./ProfileContext";
import { XP_REWARDS } from "../data/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GameContext = createContext(null);

const SAVE_KEY = "checkpoint_save_v2";
const META_KEY = "checkpoint_meta_v2";

const DEFAULT_META = { totalRuns: 0, highestFloor: 0, totalGold: 0, passifsHerites: [] };

// Champs cumulés : on garde toujours la valeur la plus haute pour ne JAMAIS perdre de progression
// (évite qu'un meta cloud périmé écrase un meta local plus récent au chargement).
const META_MAX_FIELDS = ["totalRuns", "totalDeaths", "totalKills", "totalBossKills", "totalCriticals", "totalChests", "totalSanctuaries", "totalGold", "maxRunDamage", "highestFloor",
  // Compteurs cumulés pour les succès (ne doivent jamais reculer lors d'un merge cloud)
  "totalPotions", "skillClicks", "coopChatSent", "shopsLeftEmpty", "muttonsSacrificed", "bergerRuns", "coopGamesFinished"];
const mergeMeta = (a = {}, b = {}) => {
  const out = { ...DEFAULT_META, ...a, ...b };
  for (const f of META_MAX_FIELDS) out[f] = Math.max(a[f] || 0, b[f] || 0);
  return out;
};

// === COOP SCALING ===
// HP × N, damage × 1.2^(N-1) -- applied on top of floor scaling
const applyCoopScaling = (enemyStats, playerCount) => {
  if (!playerCount || playerCount <= 1) return enemyStats;
  const N = playerCount;
  const dmgMult = Math.pow(1.2, N - 1);
  return {
    ...enemyStats,
    pv: Math.max(1, Math.round(enemyStats.pv * N)),
    pvMax: Math.max(1, Math.round((enemyStats.pvMax || enemyStats.pv) * N)),
    atk: Math.max(1, Math.round(enemyStats.atk * dmgMult)),
    degatsElem: Math.max(0, Math.round((enemyStats.degatsElem || 0) * dmgMult)),
  };
};

export const GameProvider = ({ children }) => {
  const { user } = useAuth();
  const coop = useCoop();
  const { grantXP, grantMetaGold, unlockAchievement, profile } = useProfile();
  const coopMode = !!coop?.coopMode;
  const coopState = coop?.lobby?.game_state || null;
  const stateRevision = coop?.lobby?.state_revision;

  // ===== LOCAL (solo) STATE =====
  const [character, setCharacter] = useState(null);
  const [map, setMap] = useState([]);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [floor, setFloor] = useState(1);
  const [currentEncounter, setCurrentEncounter] = useState(null);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [gameLoaded, setGameLoaded] = useState(false);
  const [cloudSyncing, setCloudSyncing] = useState(false);
  const syncTimerRef = useRef(null);
  const lastUserIdRef = useRef(null);
  // Incrémenté à chaque démarrage de run local : permet à la synchro cloud (async)
  // de NE PAS écraser une run fraîchement lancée par le joueur pendant le fetch.
  const runEpochRef = useRef(0);

  // ===== INITIAL LOAD (local) =====
  useEffect(() => {
    try {
      const savedMeta = localStorage.getItem(META_KEY);
      if (savedMeta) setMeta({ ...DEFAULT_META, ...JSON.parse(savedMeta) });
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setCharacter(data.character);
        if (data.character?.themeOffset != null) setRunThemeOffset(data.character.themeOffset);
        setMap(data.map);
        setCurrentNodeId(data.currentNodeId);
        setFloor(data.floor);
        setCurrentEncounter(data.currentEncounter);
      }
    } catch (e) {
      console.warn("Save load failed", e);
    }
    setGameLoaded(true);
  }, []);

  // ===== CLOUD SYNC (only solo) =====
  useEffect(() => {
    if (!gameLoaded || !user || coopMode) return;
    if (lastUserIdRef.current === user.user_id) return;
    lastUserIdRef.current = user.user_id;

    (async () => {
      try {
        setCloudSyncing(true);
        const res = await axios.get(`${API}/save`, { withCredentials: true });
        const cloudData = res.data?.data;
        if (cloudData) {
          if (cloudData.meta) setMeta((localM) => mergeMeta(localM, cloudData.meta));
          // Ne restaure l'ancienne sauvegarde QUE si le joueur n'a lancé aucune nouvelle
          // run cette session (runEpochRef === 0). Sinon on écraserait le perso/sprite
          // fraîchement choisi par l'ancienne classe sauvegardée (bug "toujours Duelliste").
          if (cloudData.character && runEpochRef.current === 0) {
            setCharacter(cloudData.character);
            if (cloudData.character.themeOffset != null) setRunThemeOffset(cloudData.character.themeOffset);
            setMap(cloudData.map || []);
            setCurrentNodeId(cloudData.currentNodeId || null);
            setFloor(cloudData.floor || 1);
            setCurrentEncounter(cloudData.currentEncounter || null);
          }
        } else {
          await axios.post(
            `${API}/save`,
            { data: { character, map, currentNodeId, floor, currentEncounter, meta } },
            { withCredentials: true }
          );
        }
      } catch (e) {
        console.warn("Cloud sync failed", e);
      } finally {
        setCloudSyncing(false);
      }
    })();
    // eslint-disable-next-line
  }, [user, gameLoaded, coopMode]);

  useEffect(() => {
    if (!gameLoaded || !user || coopMode) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      axios.post(
        `${API}/save`,
        { data: { character, map, currentNodeId, floor, currentEncounter, meta } },
        { withCredentials: true }
      ).catch(() => {});
    }, 2500);
    return () => { if (syncTimerRef.current) clearTimeout(syncTimerRef.current); };
  }, [character, map, currentNodeId, floor, currentEncounter, meta, gameLoaded, user, coopMode]);

  useEffect(() => {
    if (!gameLoaded || coopMode) return;
    if (character) {
      localStorage.setItem(SAVE_KEY, JSON.stringify({ character, map, currentNodeId, floor, currentEncounter }));
    }
  }, [character, map, currentNodeId, floor, currentEncounter, gameLoaded, coopMode]);

  useEffect(() => {
    if (!gameLoaded) return;
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  }, [meta, gameLoaded]);

  // ===== COOP STATE PROJECTION =====
  // When coopMode, the visible state comes from coop.lobby.game_state.
  const coopCharacter = useMemo(() => {
    if (!coopMode || !coopState || !user) return null;
    return coopState.players?.[user.user_id]?.character || null;
  }, [coopMode, coopState, user]);
  const coopMap = coopState?.map || [];
  const coopCurrentNodeId = coopState?.currentNodeId || null;
  const coopFloor = coopState?.floor || 1;
  const coopEncounter = coopState?.currentEncounter || null;

  const viewCharacter = coopMode ? coopCharacter : character;
  const viewMap = coopMode ? coopMap : map;
  const viewCurrentNodeId = coopMode ? coopCurrentNodeId : currentNodeId;
  const viewFloor = coopMode ? coopFloor : floor;
  const viewEncounter = coopMode ? coopEncounter : currentEncounter;

  // ===== HELPERS for coop publish =====
  const publishCoop = useCallback((producer) => {
    const cur = coop?.lobby?.game_state;
    if (!cur) return false;
    const next = producer(structuredClone(cur));
    if (!next) return false;
    return coop.publishState(next, { expectedRevision: stateRevision });
  }, [coop, stateRevision]);

  // ===== SUCCÈS : compteurs cumulés & ensembles =====
  // bumpMeta : incrémente un compteur méta et débloque les succès dont le seuil est atteint.
  // unlockMap = { seuil: "achievement_id" }
  const bumpMeta = useCallback((field, by = 1, unlockMap = {}) => {
    setMeta((m) => {
      const val = (m[field] || 0) + by;
      for (const [threshold, achId] of Object.entries(unlockMap)) {
        if (val >= Number(threshold)) unlockAchievement?.(achId);
      }
      return { ...m, [field]: val };
    });
  }, [unlockAchievement]);

  // bumpMetaSet : ajoute une valeur à un ensemble (tableau dédupliqué) et débloque
  // le succès quand l'ensemble atteint la taille requise.
  const bumpMetaSet = useCallback((field, value, neededSize, achId) => {
    if (value == null) return;
    setMeta((m) => {
      const arr = Array.from(new Set([...(m[field] || []), value]));
      if (arr.length >= neededSize) unlockAchievement?.(achId);
      return { ...m, [field]: arr };
    });
  }, [unlockAchievement]);


  // ===== CHARACTER / FLOW ACTIONS =====
  const startNewRun = useCallback((classeId) => {
    if (coopMode) {
      // In coop, run start is managed by lobby start_game. Ignore.
      return;
    }
    // No passive inheritance at all — every run is a fresh start.
    runEpochRef.current += 1; // signale une nouvelle run (empêche l'écrasement par la synchro cloud)
    const newChar = createCharacter(classeId, CLASSES, []);
    // Randomize biome ordering for this run
    const offset = Math.floor(Math.random() * FLOOR_THEMES.length);
    setRunThemeOffset(offset);
    newChar.themeOffset = offset;
    const newMap = generateFloorMap(1);
    setCharacter(newChar);
    setMap(newMap);
    setCurrentNodeId(null);
    setFloor(1);
    setCurrentEncounter(null);
    setMeta((m) => ({
      ...m,
      totalRuns: m.totalRuns + 1,
      classesPlayed: Array.from(new Set([...(m.classesPlayed || []), classeId])),
      runStartedAt: Date.now(),
      floorStartedAt: Date.now(),
      tookDamageThisFloor: false,
      lastDeath: null,
      passifsHerites: [],
    }));
    unlockAchievement?.("first_steps");
    // Sheep whisperer : 10 runs avec le Berger
    if (classeId === "berger") bumpMeta("bergerRuns", 1, { 10: "sheep_whisperer" });
    if (user) reportMissionEvent("run_start", 1);
    setMeta((m) => {
      const played = new Set([...(m.classesPlayed || []), classeId]);
      if (played.size >= Object.keys(CLASSES).length) {
        unlockAchievement?.("all_classes");
      }
      return m;
    });
  }, [coopMode, unlockAchievement, user, bumpMeta]);

  const selectNode = useCallback((nodeId) => {
    if (coopMode) {
      publishCoop((s) => {
        let selectedNode = null;
        for (const row of s.map) for (const n of row) if (n.id === nodeId) selectedNode = n;
        if (!selectedNode) return null;
        s.currentNodeId = nodeId;
        // Apply per-tile passives to ALL players' characters
        for (const uid of Object.keys(s.players || {})) {
          const pc = s.players[uid].character;
          if (pc) s.players[uid].character = applyPassivesPerTile(pc);
        }
        const N = s.playerCount || Object.keys(s.players || {}).length || 1;
        if (selectedNode.type === "combat" || (selectedNode.type === "elite" && s.floor < 5)) {
          const enemy = getRandomEnemy(false);
          let scaled = scaleEnemyStats(enemy.baseStats, s.floor);
          scaled = applyCoopScaling(scaled, N);
          s.currentEncounter = { type: "combat", enemy: { ...enemy, stats: scaled, pvCurrent: scaled.pv } };
        } else if (selectedNode.type === "elite") {
          const enemy = getRandomEnemy(true);
          let scaled = scaleEnemyStats(enemy.baseStats, s.floor + 1);
          scaled = applyCoopScaling(scaled, N);
          s.currentEncounter = { type: "combat", enemy: { ...enemy, stats: scaled, pvCurrent: scaled.pv, isElite: true } };
        } else if (selectedNode.type === "boss") {
          const boss = getBossForFloor(s.floor);
          let scaled = scaleEnemyStats(boss.baseStats, s.floor);
          scaled = applyCoopScaling(scaled, N);
          s.currentEncounter = { type: "combat", enemy: { ...boss, stats: scaled, pvCurrent: scaled.pv, isBoss: true } };
        } else if (selectedNode.type === "chest") {
          const rarity = rollChestRarity(s.floor);
          const content = generateChestContent(rarity);
          s.currentEncounter = { type: "chest", content, nodeId };
        } else if (selectedNode.type === "sanctuary") {
          const rarity = rollSanctuaryRarity(s.floor);
          const options = [generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity)];
          s.currentEncounter = { type: "sanctuary", rarity, options, nodeId };
        } else if (selectedNode.type === "shop") {
          const shopId = `shop_${s.floor}_${nodeId}`;
          const stock = generateShop(s.floor);
          s.currentEncounter = { type: "shop", shopId, stock, nodeId };
        }
        return s;
      });
      return;
    }

    // Solo path
    let selectedNode = null;
    for (const row of map) for (const n of row) if (n.id === nodeId) selectedNode = n;
    if (!selectedNode) return;

    setCurrentNodeId(nodeId);
    setCharacter((char) => char ? applyPassivesPerTile(char) : char);

    if (selectedNode.type === "combat" || (selectedNode.type === "elite" && floor < 5)) {
      const enemy = getRandomEnemy(false);
      const scaled = scaleEnemyStats(enemy.baseStats, floor);
      setCurrentEncounter({ type: "combat", enemy: { ...enemy, stats: scaled, pvCurrent: scaled.pv } });
    } else if (selectedNode.type === "elite") {
      const enemy = getRandomEnemy(true);
      const scaled = scaleEnemyStats(enemy.baseStats, floor + 1);
      setCurrentEncounter({ type: "combat", enemy: { ...enemy, stats: scaled, pvCurrent: scaled.pv, isElite: true } });
    } else if (selectedNode.type === "boss") {
      const boss = getBossForFloor(floor);
      const scaled = scaleEnemyStats(boss.baseStats, floor);
      setCurrentEncounter({ type: "combat", enemy: { ...boss, stats: scaled, pvCurrent: scaled.pv, isBoss: true } });
    } else if (selectedNode.type === "chest") {
      const rarity = rollChestRarity(floor);
      const content = generateChestContent(rarity);
      setCurrentEncounter({ type: "chest", content, nodeId });
    } else if (selectedNode.type === "sanctuary") {
      const rarity = rollSanctuaryRarity(floor);
      const options = [generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity)];
      setCurrentEncounter({ type: "sanctuary", rarity, options, nodeId });
    } else if (selectedNode.type === "shop") {
      const shopId = `shop_${floor}_${nodeId}`;
      const econome = (character?.passifs || []).find((p) => p.special === "shop_discount");
      const discount = econome ? (econome.value || 0) / 100 : 0;
      const stock = generateShop(floor, discount);
      setCurrentEncounter({ type: "shop", shopId, stock, nodeId });
    }
  }, [coopMode, publishCoop, map, floor, character]);

  const completeEncounter = useCallback((rewards = {}) => {
    if (coopMode) {
      publishCoop((s) => {
        if (s.currentNodeId) {
          s.map = s.map.map((row) => row.map((n) => n.id === s.currentNodeId ? { ...n, completed: true } : n));
        }
        const isBoss = s.currentEncounter?.type === "combat" && s.currentEncounter?.enemy?.isBoss;
        const wasCombat = s.currentEncounter?.type === "combat";
        if (wasCombat) {
          for (const uid of Object.keys(s.players || {})) {
            const pc = s.players[uid]?.character;
            if (!pc) continue;
            let updated = applyPassivesAfterCombat(pc);
            if (rewards.gold && uid === user?.user_id) updated.gold = (updated.gold || 0) + rewards.gold;
            s.players[uid].character = updated;
          }
        }
        s.currentEncounter = null;
        if (isBoss) {
          for (const uid of Object.keys(s.players || {})) {
            const pc = s.players[uid]?.character;
            if (!pc) continue;
            const healed = applyPassivesOnFloorAdvance({ ...pc, stats: { ...pc.stats } });
            healed.stats.pv = Math.min(healed.stats.pvMax, healed.stats.pv + Math.round(healed.stats.pvMax * 0.4));
            healed.floor = s.floor + 1;
            s.players[uid].character = healed;
          }
          const newFloor = s.floor + 1;
          s.floor = newFloor;
          s.map = generateFloorMap(newFloor);
          s.currentNodeId = null;
        }
        return s;
      });
      return;
    }

    setMap((m) => m.map((row) => row.map((n) => n.id === currentNodeId ? { ...n, completed: true } : n)));

    const wasCombat = currentEncounter?.type === "combat";
    const enemyForRewards = wasCombat ? currentEncounter?.enemy : null;
    const isElite = !!enemyForRewards?.isElite;
    const isBoss = !!enemyForRewards?.isBoss;

    // === Missions (comptes connectés uniquement) ===
    if (user) {
      if (wasCombat) {
        reportMissionEvent("combat_win", 1);
        if (isElite) reportMissionEvent("elite_kill", 1);
        if (isBoss) reportMissionEvent("boss_kill", 1);
        const dmg = Math.round(rewards.damageDealt || enemyForRewards?.stats?.pvMax || 0);
        if (dmg > 0) reportMissionEvent("damage_dealt", dmg);
      } else if (currentEncounter?.type === "chest") {
        reportMissionEvent("chest_open", 1);
      } else if (currentEncounter?.type === "sanctuary") {
        reportMissionEvent("sanctuary_visit", 1);
      }
    }

    // === RUN XP awards (separate from META profile XP) ===
    // Ennemis: 30, Elite: 60, Boss: 100, Coffre: 12, Sanctuaire: 12, Shop: 0
    let runXpAmount = 0;
    let runXpReason = "";
    if (wasCombat) {
      runXpAmount = isBoss ? 100 : isElite ? 60 : 30;
      runXpReason = isBoss ? "Boss vaincu" : isElite ? "Élite vaincu" : "Combat gagné";
    } else if (currentEncounter?.type === "chest") {
      runXpAmount = 12;
      runXpReason = "Coffre ouvert";
    } else if (currentEncounter?.type === "sanctuary") {
      runXpAmount = 12;
      runXpReason = "Sanctuaire visité";
    }

    if (wasCombat) {
      const ctx = { noDamageTaken: !!rewards.noDamageTaken };
      // Succès : éléments infligés (6) & classes ayant gagné un combat (6)
      const wonClass = CLASSES[character?.classeId];
      if (wonClass?.element) bumpMetaSet("elementsDealt", wonClass.element, 6, "elemental");
      if (character?.classeId) bumpMetaSet("classesWon", character.classeId, 6, "elemental_master");
      // Drapeau "dégâts subis cet étage" (pour pacifist_floor)
      if (!rewards.noDamageTaken) setMeta((m) => ({ ...m, tookDamageThisFloor: true }));
      setCharacter((char) => {
        if (!char) return char;
        let updated = applyPassivesAfterCombat(char, ctx);
        if (isBoss) updated = applyPassivesAfterRoomDone(updated, "boss", ctx);
        else if (isElite) updated = applyPassivesAfterRoomDone(updated, "elite", ctx);
        if (rewards.gold) updated.gold += rewards.gold;
        // Grant run XP & level up
        if (runXpAmount > 0) {
          const { character: c2, levelsGained } = grantRunXp(updated, runXpAmount);
          updated = c2;
          if (levelsGained > 0) {
            setTimeout(() => {
              import("sonner").then(({ toast }) => {
                toast.success(`Niveau ${c2.runLevel} atteint !`, {
                  description: `+${5 * levelsGained} PV max et +${levelsGained} à toutes les autres stats.`,
                  duration: 3500,
                });
              });
            }, 100);
          }
        }
        return updated;
      });
      const xp = isBoss
        ? XP_REWARDS.boss_won(floor)
        : isElite
          ? XP_REWARDS.elite_won(floor)
          : XP_REWARDS.combat_won(floor);
      grantXP?.(xp, runXpReason);
    } else if (currentEncounter?.type === "chest") {
      setCharacter((char) => {
        if (!char) return char;
        let updated = applyPassivesAfterRoomDone(char, "chest");
        if (runXpAmount > 0) updated = grantRunXp(updated, runXpAmount).character;
        return updated;
      });
      grantXP?.(XP_REWARDS.chest_opened, "Coffre ouvert");
    } else if (currentEncounter?.type === "sanctuary") {
      setCharacter((char) => {
        if (!char) return char;
        let updated = applyPassivesAfterRoomDone(char, "sanctuary");
        if (runXpAmount > 0) updated = grantRunXp(updated, runXpAmount).character;
        return updated;
      });
      grantXP?.(XP_REWARDS.sanctuary_visited, "Sanctuaire visité");
    }

    setCurrentEncounter(null);

    if (isBoss) {
      setCharacter((char) => {
        if (!char) return char;
        const healed = applyPassivesOnFloorAdvance({ ...char, stats: { ...char.stats } });
        healed.stats.pv = Math.min(healed.stats.pvMax, healed.stats.pv + Math.round(healed.stats.pvMax * 0.4));
        healed.floor = floor + 1;
        return healed;
      });
      const newFloor = floor + 1;
      setFloor(newFloor);
      setMap(generateFloorMap(newFloor));
      setCurrentNodeId(null);
      if (user) reportMissionEvent("floor_reach", newFloor);
      setMeta((m) => ({ ...m, highestFloor: Math.max(m.highestFloor, newFloor - 1) }));
      // Floor cleared XP + milestone achievements
      grantXP?.(XP_REWARDS.floor_cleared, `Étage ${floor} terminé`);
      if (newFloor >= 5) unlockAchievement?.("floor_5");
      if (newFloor >= 10) unlockAchievement?.("floor_10");
      if (newFloor >= 15) unlockAchievement?.("floor_15");
      if (newFloor >= 20) unlockAchievement?.("floor_20");
      if (newFloor >= 25) unlockAchievement?.("legend_floor");
      if (newFloor >= 30) unlockAchievement?.("floor_30");
      if (newFloor >= 50) unlockAchievement?.("floor_50");
      if (newFloor >= 100) unlockAchievement?.("floor_100");
      // Speedrun check
      const runStart = meta.runStartedAt;
      if (runStart && newFloor >= 5 && (Date.now() - runStart) < 10 * 60 * 1000) {
        unlockAchievement?.("speedrun_5");
      }
      // === Succès liés à l'étage franchi ===
      const rs2 = character?.runStats || {};
      // Tank n spank : 1000 dégâts encaissés cumulés sur la run (et toujours en vie)
      if ((rs2.totalDamageTaken || 0) >= 1000) unlockAchievement?.("tank_n_spank");
      // Pacifist floor : étage nettoyé sans subir un seul dégât
      if (!meta.tookDamageThisFloor) unlockAchievement?.("pacifist_floor");
      // Speed demon : étage nettoyé en moins de 60s
      if (meta.floorStartedAt && (Date.now() - meta.floorStartedAt) < 60 * 1000) unlockAchievement?.("speed_demon");
      // Frugal winner : boss vaincu sans avoir acheté de compétence
      if ((rs2.skillsPurchased || 0) === 0) unlockAchievement?.("frugal_winner");
      // Tourist : 6 types de biomes visités
      bumpMetaSet("biomesVisited", themeForFloor(floor).id, 6, "tourist");
      // Réinitialise les drapeaux pour le nouvel étage
      setMeta((m) => ({ ...m, floorStartedAt: Date.now(), tookDamageThisFloor: false }));
    }
  }, [coopMode, publishCoop, user, currentNodeId, currentEncounter, floor, grantXP, unlockAchievement, meta, character, bumpMetaSet]);

  const updateCharacter = useCallback((updater) => {
    if (coopMode) {
      publishCoop((s) => {
        const uid = user?.user_id;
        if (!uid || !s.players?.[uid]) return null;
        const cur = s.players[uid].character;
        const next = typeof updater === "function" ? updater(cur) : updater;
        s.players[uid].character = next;
        return s;
      });
      return;
    }
    setCharacter((c) => typeof updater === "function" ? updater(c) : updater);
  }, [coopMode, publishCoop, user]);

  const takeChestRewards = useCallback((content) => {
    if (coopMode) {
      publishCoop((s) => {
        const uid = user?.user_id;
        if (uid && s.players?.[uid]) {
          let updated = applyChestBonuses(s.players[uid].character, content.stats);
          updated.gold = (updated.gold || 0) + content.gold;
          const rs = { ...(updated.runStats || {}) };
          rs.chestsOpened = (rs.chestsOpened || 0) + 1;
          rs.goldEarned = (rs.goldEarned || 0) + content.gold;
          updated.runStats = rs;
          s.players[uid].character = updated;
        }
        if (s.currentNodeId) {
          s.map = s.map.map((row) => row.map((n) => n.id === s.currentNodeId ? { ...n, completed: true } : n));
        }
        s.currentEncounter = null;
        return s;
      });
      return;
    }
    setCharacter((c) => {
      if (!c) return c;
      let updated = applyChestBonuses(c, content.stats);
      updated.gold = (updated.gold || 0) + content.gold;
      // Drop an item (uses content.item if pre-rolled, else roll now)
      const droppedItem = content.item;
      if (droppedItem) {
        updated = addItemToInventory(updated, droppedItem);
      }
      const rs = { ...(updated.runStats || {}) };
      rs.chestsOpened = (rs.chestsOpened || 0) + 1;
      rs.goldEarned = (rs.goldEarned || 0) + content.gold;
      updated.runStats = rs;
      return updated;
    });
    completeEncounter();
  }, [coopMode, publishCoop, user, completeEncounter]);

  const takeSanctuaryPassive = useCallback((passive) => {
    if (coopMode) {
      publishCoop((s) => {
        const uid = user?.user_id;
        if (uid && s.players?.[uid]) {
          const pc = s.players[uid].character;
          const rs = { ...(pc.runStats || {}) };
          rs.sanctuariesVisited = (rs.sanctuariesVisited || 0) + 1;
          s.players[uid].character = { ...pc, passifs: [...(pc.passifs || []), passive], runStats: rs };
        }
        if (s.currentNodeId) {
          s.map = s.map.map((row) => row.map((n) => n.id === s.currentNodeId ? { ...n, completed: true } : n));
        }
        s.currentEncounter = null;
        return s;
      });
      return;
    }
    setCharacter((c) => {
      if (!c) return c;
      const rs = { ...(c.runStats || {}) };
      rs.sanctuariesVisited = (rs.sanctuariesVisited || 0) + 1;
      return { ...c, passifs: [...(c.passifs || []), passive], runStats: rs };
    });
    completeEncounter();
  }, [coopMode, publishCoop, user, completeEncounter]);

  const purchaseFromShop = useCallback((itemType, itemId) => {
    if (coopMode) {
      let success = false;
      publishCoop((s) => {
        const uid = user?.user_id;
        if (!uid || !s.currentEncounter || s.currentEncounter.type !== "shop") return null;
        const me = s.players?.[uid];
        if (!me) return null;
        const stock = s.currentEncounter.stock;
        if (itemType === "potion") {
          const idx = stock.potions.findIndex((p) => p.id === itemId);
          if (idx < 0) return null;
          const potion = stock.potions[idx];
          if (potion.sold) return null;
          if (me.character.gold < potion.price) return null;
          const c = { ...me.character, stats: { ...me.character.stats } };
          c.gold -= potion.price;
          if (potion.stat === "pv") { c.stats.pvMax += potion.value; c.stats.pv += potion.value; }
          else { c.stats[potion.stat] = (c.stats[potion.stat] || 0) + potion.value; }
          me.character = c;
          s.currentEncounter.stock.potions = stock.potions.map((p) => p.id === itemId ? { ...p, sold: true } : p);
          success = true;
        } else if (itemType === "chest") {
          if (stock.chest.sold) return null;
          if (me.character.gold < stock.chest.price) return null;
          const rarity = rollChestRarity(s.floor);
          const content = generateChestContent(rarity);
          let updated = applyChestBonuses(me.character, content.stats);
          updated.gold -= stock.chest.price;
          updated.gold += content.gold;
          me.character = updated;
          s.currentEncounter.stock.chest = { ...stock.chest, sold: true, openedRarity: rarity, openedStats: content.stats, openedGold: content.gold };
          success = true;
        }
        return s;
      });
      return success;
    }
    if (!currentEncounter || currentEncounter.type !== "shop") return false;
    const stock = currentEncounter.stock;
    if (itemType === "potion") {
      const idx = stock.potions.findIndex((p) => p.id === itemId);
      if (idx < 0) return false;
      const potion = stock.potions[idx];
      if (potion.sold) return false;
      if (character.gold < potion.price) return false;
      setCharacter((c) => {
        const updated = { ...c, stats: { ...c.stats } };
        updated.gold -= potion.price;
        if (potion.stat === "pv") { updated.stats.pvMax += potion.value; updated.stats.pv += potion.value; }
        else { updated.stats[potion.stat] = (updated.stats[potion.stat] || 0) + potion.value; }
        updated.runStats = { ...(updated.runStats || {}), goldSpent: (updated.runStats?.goldSpent || 0) + potion.price };
        return updated;
      });
      setCurrentEncounter((enc) => {
        const potions = enc.stock.potions.map((p) => p.id === itemId ? { ...p, sold: true } : p);
        // Shop addict : toutes les potions de la boutique achetées
        if (potions.length >= 8 && potions.every((p) => p.sold)) unlockAchievement?.("shop_addict");
        return { ...enc, stock: { ...enc.stock, potions } };
      });
      return true;
    } else if (itemType === "chest") {
      if (stock.chest.sold) return false;
      if (character.gold < stock.chest.price) return false;
      const rarity = rollChestRarity(floor);
      const content = generateChestContent(rarity);
      setCharacter((c) => {
        let updated = applyChestBonuses(c, content.stats);
        updated.gold -= stock.chest.price;
        updated.gold += content.gold;
        if (content.item) updated = addItemToInventory(updated, content.item);
        updated.runStats = { ...(updated.runStats || {}), goldSpent: (updated.runStats?.goldSpent || 0) + stock.chest.price };
        return updated;
      });
      setCurrentEncounter((enc) => ({ ...enc, stock: { ...enc.stock, chest: { ...enc.stock.chest, sold: true, openedRarity: rarity, openedItem: content.item, openedStats: content.stats, openedGold: content.gold } } }));
      return true;
    } else if (itemType === "consumable") {
      const c = stock.consumable;
      if (!c || c.sold) return false;
      if (character.gold < c.price) return false;
      setCharacter((ch) => {
        let updated = { ...ch };
        updated.gold = (updated.gold || 0) - c.price;
        updated = addItemToInventory(updated, c.itemId);
        updated.runStats = { ...(updated.runStats || {}), goldSpent: (updated.runStats?.goldSpent || 0) + c.price };
        return updated;
      });
      setCurrentEncounter((enc) => ({ ...enc, stock: { ...enc.stock, consumable: { ...enc.stock.consumable, sold: true } } }));
      return true;
    } else if (itemType === "competence") {
      // itemId = competence.id
      const cls = CLASSES[character.classeId];
      const comp = cls?.competences?.find((c) => c.id === itemId);
      if (!comp || !comp.purchasable) return false;
      const alreadyOwned = (character.purchasedCompetences || []).includes(itemId);
      if (alreadyOwned) return false;
      if (character.gold < (comp.purchaseCost || 0)) return false;
      setCharacter((ch) => {
        const updated = { ...ch };
        updated.gold = (updated.gold || 0) - comp.purchaseCost;
        updated.purchasedCompetences = [...(ch.purchasedCompetences || []), itemId];
        updated.runStats = { ...(updated.runStats || {}), goldSpent: (updated.runStats?.goldSpent || 0) + (comp.purchaseCost || 0), skillsPurchased: (updated.runStats?.skillsPurchased || 0) + 1 };
        return updated;
      });
      return true;
    }
    return false;
  }, [coopMode, publishCoop, user, currentEncounter, character, floor, unlockAchievement]);

  const leaveEncounter = useCallback(() => { completeEncounter(); }, [completeEncounter]);

  // Utilise un item de l'inventaire (effet hors combat — heal, perm stat, run_xp).
  // Pour les items "inCombat only" (bomb, smoke_bomb), retourne false (à gérer dans Combat).
  const useInventoryItem = useCallback((uuid) => {
    const cur = character;
    if (!cur) return false;
    const entry = (cur.inventory || []).find((i) => i.uuid === uuid);
    if (!entry) return false;
    const item = ITEMS[entry.id];
    if (!item) return false;
    if (!item.everywhere) return false; // doit être utilisé via Combat.jsx
    setCharacter((c) => {
      if (!c) return c;
      const updated = applyItemEffectOutOfCombat(c, item);
      return removeItemFromInventory(updated, uuid);
    });
    return true;
  }, [character]);

  const handleDeath = useCallback(() => {
    if (coopMode) {
      // In coop, dead players sit out for the rest of the run; meta still updates per-user.
      return;
    }
    // Phoenix Feather : si le perso a un revive token, le consommer pour le ressusciter à 50% PV
    if (character && (character.reviveTokens || 0) > 0) {
      setCharacter((c) => {
        if (!c) return c;
        const updated = { ...c, stats: { ...c.stats } };
        updated.reviveTokens = (c.reviveTokens || 1) - 1;
        updated.stats.pv = Math.max(1, Math.ceil(updated.stats.pvMax * 0.5));
        return updated;
      });
      import("sonner").then(({ toast }) => {
        toast.success("🪶 Plume de Phénix consommée !", {
          description: "Tu reviens à la vie à 50% PV.",
          duration: 4000,
        });
      });
      return;
    }
    let runEndMeta = null;
    if (character) {
      const rs = character.runStats || {};
      const totalEarnedThisRun = (character.gold || 0); // gold owned at death (already includes farmed coins)
      // Grant meta gold (cumulative menu-shop currency)
      if (totalEarnedThisRun > 0) grantMetaGold?.(totalEarnedThisRun);
      runEndMeta = {
        ...meta,
        totalGold: (meta.totalGold || 0) + (character.gold || 0),
        // No passive inheritance anywhere — runs are fully one-shot.
        lastDeath: null,
        passifsHerites: [],
        totalDeaths: (meta.totalDeaths || 0) + 1,
        totalKills: (meta.totalKills || 0) + (rs.kills || 0),
        totalBossKills: (meta.totalBossKills || 0) + (rs.bossesDefeated || 0),
        totalCriticals: (meta.totalCriticals || 0) + (rs.criticalsLanded || 0),
        totalChests: (meta.totalChests || 0) + (rs.chestsOpened || 0),
        totalSanctuaries: (meta.totalSanctuaries || 0) + (rs.sanctuariesVisited || 0),
        totalRuns: (meta.totalRuns || 0) + 1,
        maxRunDamage: Math.max(meta.maxRunDamage || 0, rs.totalDamageDealt || 0),
      };
      setMeta(runEndMeta);

      // === Achievement checks at run end ===
      // Phoenix (10 deaths)
      const willHaveDeaths = (meta.totalDeaths || 0) + 1;
      if (willHaveDeaths >= 10) unlockAchievement?.("phoenix");
      // Bestiary (50 enemies cumulative)
      if (((meta.totalKills || 0) + (rs.kills || 0)) >= 50) unlockAchievement?.("bestiary");
      // Boss killer (5 boss cumulative)
      if (((meta.totalBossKills || 0) + (rs.bossesDefeated || 0)) >= 5) unlockAchievement?.("boss_killer");
      // Crit storm (100 crits cumulative)
      if (((meta.totalCriticals || 0) + (rs.criticalsLanded || 0)) >= 100) unlockAchievement?.("crit_storm");
      // Big hit
      if ((rs.maxHit || 0) >= 200) unlockAchievement?.("big_hit");
      // Marathon damage
      if ((rs.totalDamageDealt || 0) >= 9999) unlockAchievement?.("marathon_dmg");
      // Burn master
      if ((rs.burnDamage || 0) >= 200) unlockAchievement?.("burn_master");
      // Collector (20 passifs)
      if ((character.passifs?.length || 0) >= 20) unlockAchievement?.("collector");
      // Tycoon (1000 gold ever held)
      if ((character.gold || 0) >= 1000) unlockAchievement?.("tycoon");
      // Broke
      if ((character.gold || 0) === 0) unlockAchievement?.("broke");
      // Night owl
      const hr = new Date().getHours();
      if (hr >= 3 && hr < 5) unlockAchievement?.("night_owl");
      if (hr >= 5 && hr < 7) unlockAchievement?.("early_bird");

      // === Nouveaux paliers cumulés ===
      const totKills = (meta.totalKills || 0) + (rs.kills || 0);
      if (totKills >= 100) unlockAchievement?.("kills_100");
      if (totKills >= 250) unlockAchievement?.("kills_250");
      if (totKills >= 500) unlockAchievement?.("kills_500");
      if (totKills >= 1000) unlockAchievement?.("kills_1000");
      const totBoss = (meta.totalBossKills || 0) + (rs.bossesDefeated || 0);
      if (totBoss >= 10) unlockAchievement?.("boss_10");
      if (totBoss >= 25) unlockAchievement?.("boss_25");
      if (totBoss >= 50) unlockAchievement?.("boss_50");
      const totCrit = (meta.totalCriticals || 0) + (rs.criticalsLanded || 0);
      if (totCrit >= 250) unlockAchievement?.("crit_250");
      if (totCrit >= 500) unlockAchievement?.("crit_500");
      if (totCrit >= 1000) unlockAchievement?.("crit_1000");
      if (willHaveDeaths >= 25) unlockAchievement?.("deaths_25");
      if (willHaveDeaths >= 50) unlockAchievement?.("deaths_50");
      if (willHaveDeaths >= 100) unlockAchievement?.("deaths_100");
      if ((character.gold || 0) >= 5000) unlockAchievement?.("gold_5000");
      if ((character.gold || 0) >= 10000) unlockAchievement?.("gold_10000");
      if ((character.gold || 0) >= 50000) unlockAchievement?.("gold_50000");
      if ((character.passifs?.length || 0) >= 30) unlockAchievement?.("passifs_30");
      if ((character.passifs?.length || 0) >= 40) unlockAchievement?.("passifs_40");
      if ((rs.maxHit || 0) >= 500) unlockAchievement?.("maxhit_500");
      if ((rs.maxHit || 0) >= 1000) unlockAchievement?.("maxhit_1000");
      if ((rs.maxHit || 0) >= 5000) unlockAchievement?.("maxhit_5000");
      if ((rs.totalDamageDealt || 0) >= 50000) unlockAchievement?.("dmg_run_50000");
      if ((rs.burnDamage || 0) >= 500) unlockAchievement?.("burn_500");
      const totChests = (meta.totalChests || 0) + (rs.chestsOpened || 0);
      if (totChests >= 25) unlockAchievement?.("chests_25");
      if (totChests >= 100) unlockAchievement?.("chests_100");
      const totSanct = (meta.totalSanctuaries || 0) + (rs.sanctuariesVisited || 0);
      if (totSanct >= 25) unlockAchievement?.("sanct_25");
      if (totSanct >= 100) unlockAchievement?.("sanct_100");
      const totGoldEver = (meta.totalGold || 0) + (character.gold || 0);
      if (totGoldEver >= 100000) unlockAchievement?.("the_economist");
      if (((meta.totalRuns || 0) + 1) >= 50) unlockAchievement?.("marathoner");
      // Mourir avec de l'or = perdre avec classe
      if ((character.gold || 0) > 0) unlockAchievement?.("stylish_loss");
      if ((character.gold || 0) === 100) unlockAchievement?.("round_number");
      // Hoarder : finir la run sans avoir dépensé un seul or
      if ((character.runStats?.goldSpent || 0) === 0) unlockAchievement?.("hoarder");
      // Mourir à l'étage 13
      if (floor === 13) unlockAchievement?.("unlucky_13");
      // Stats extrêmes
      if ((character.stats?.atk || 0) >= 30 && (character.stats?.pvMax || 0) < 5) unlockAchievement?.("glass_cannon");
      if ((character.stats?.def || 0) >= 40) unlockAchievement?.("immortal_wall");
      // Paliers de niveau de profil
      if ((profile?.level || 1) >= 10) unlockAchievement?.("noob_no_more");
      if ((profile?.level || 1) >= 30) unlockAchievement?.("the_chosen_one");
      if ((profile?.level || 1) >= 50) unlockAchievement?.("veteran");
    }
    setCharacter(null);
    setMap([]);
    setCurrentNodeId(null);
    setFloor(1);
    setCurrentEncounter(null);
    localStorage.removeItem(SAVE_KEY);
    // Sauvegarde cloud IMMÉDIATE des stats de fin de run (sinon perdues si l'utilisateur
    // rafraîchit/ferme avant l'expiration du debounce de 2,5s → classement non mis à jour).
    if (user && !coopMode && runEndMeta) {
      axios
        .post(
          `${API}/save`,
          { data: { character: null, map: [], currentNodeId: null, floor: 1, currentEncounter: null, meta: runEndMeta } },
          { withCredentials: true }
        )
        .catch(() => {});
    }
  }, [coopMode, character, meta, unlockAchievement, grantMetaGold, floor, profile, user]);

  const resetSave = useCallback(() => {
    setCharacter(null);
    setMap([]);
    setCurrentNodeId(null);
    setFloor(1);
    setCurrentEncounter(null);
    localStorage.removeItem(SAVE_KEY);
  }, []);

  const resetAllProgress = useCallback(() => {
    setMeta(DEFAULT_META);
    resetSave();
    localStorage.removeItem(META_KEY);
  }, [resetSave]);

  return (
    <GameContext.Provider
      value={{
        character: viewCharacter,
        map: viewMap,
        currentNodeId: viewCurrentNodeId,
        floor: viewFloor,
        currentEncounter: viewEncounter,
        meta,
        gameLoaded,
        coopMode,
        coopState,
        startNewRun,
        selectNode,
        updateCharacter,
        completeEncounter,
        takeChestRewards,
        takeSanctuaryPassive,
        purchaseFromShop,
        leaveEncounter,
        handleDeath,
        resetSave,
        resetAllProgress,
        cloudSyncing,
        useInventoryItem,
        bumpMeta,
        bumpMetaSet,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
};
